import * as React from 'react';
import { logsApi } from '@api/logs';
import type { LogEntry, LogFilters } from '@/types';

interface UseLogTailOptions {
    cursor: string | null;
    enabled: boolean;
    filters: LogFilters;
    isPaused?: boolean;
    onLogs: (logs: LogEntry[]) => void;
}

interface UseLogTailResult {
    error: Error | null;
    isCatchingUp: boolean;
    isFetching: boolean;
}

const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 100;
const MAX_CATCH_UP_PAGES = 8;

export function useLogTail({ cursor, enabled, filters, isPaused = false, onLogs }: UseLogTailOptions): UseLogTailResult {
    const abortRef = React.useRef<AbortController | null>(null);
    const [error, setError] = React.useState<Error | null>(null);
    const [isFetching, setIsFetching] = React.useState(false);
    const [isCatchingUp, setIsCatchingUp] = React.useState(false);

    const stopPolling = React.useEffectEvent(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setIsFetching(false);
        setIsCatchingUp(false);
    });

    const pollForNewerLogs = React.useEffectEvent(async (reason: 'interval' | 'visibility') => {
        if (!enabled || isPaused || !cursor || abortRef.current) {
            return;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setError(null);
        setIsFetching(true);
        setIsCatchingUp(reason === 'visibility');

        let nextCursor = cursor;
        let page = 0;

        try {
            while (page < MAX_CATCH_UP_PAGES) {
                const result = await logsApi.getLogWindow(
                    filters,
                    {
                        cursor: nextCursor,
                        direction: 'newer',
                        limit: PAGE_SIZE
                    },
                    controller.signal
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (result.logs.length === 0) {
                    break;
                }

                onLogs(result.logs);
                nextCursor = result.logs[0]?.cursor ?? nextCursor;
                page += 1;

                if (!result.hasMore) {
                    break;
                }
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            setError(error instanceof Error ? error : new Error('Failed to fetch new logs'));
        } finally {
            if (abortRef.current === controller) {
                abortRef.current = null;
                setIsFetching(false);
                setIsCatchingUp(false);
            }
        }
    });

    React.useEffect(() => {
        if (!enabled || isPaused || !cursor) {
            stopPolling();
            return;
        }

        let timeoutId: number | null = null;
        let cancelled = false;

        const scheduleNextPoll = () => {
            timeoutId = window.setTimeout(async () => {
                if (cancelled) {
                    return;
                }

                if (!document.hidden) {
                    await pollForNewerLogs('interval');
                }

                if (!cancelled) {
                    scheduleNextPoll();
                }
            }, POLL_INTERVAL_MS);
        };

        scheduleNextPoll();

        return () => {
            cancelled = true;
            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, [cursor, enabled, filters, isPaused]);

    React.useEffect(() => {
        if (!enabled || !cursor) {
            return;
        }

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                void pollForNewerLogs('visibility');
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [cursor, enabled]);

    React.useEffect(() => {
        return () => {
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, []);

    return {
        error,
        isCatchingUp,
        isFetching
    };
}
